import { useState } from 'react';
import { CaretDownIcon, QuestionIcon } from '@phosphor-icons/react';
import { T } from 'gt-react';

import { useHelpMenuSlotTarget } from '@/components/common/HelpMenu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
} from '@/components/ui/sidebar';

/**
 * When true, help items sit behind a collapsible "Help" section (starts closed).
 * Set to false to always show help actions, including in icon-only sidebar mode.
 * Flip to a feature flag here when we want this configurable per org.
 */
const HELP_MENU_COLLAPSIBLE = false;

function HelpSectionLabel({ collapsible }: { collapsible: boolean }) {
  const label = (
    <span className="flex items-center gap-2">
      <QuestionIcon aria-hidden="true" />
      <T context="sidebar section heading for help and support actions">Help</T>
    </span>
  );

  if (collapsible) {
    return (
      <CollapsibleTrigger asChild>
        <SidebarGroupLabel className="group/help cursor-pointer justify-between group-data-[collapsible=icon]:hidden">
          {label}
          <CaretDownIcon
            className="transition-transform -rotate-90 group-data-[panel-open]/help:rotate-0"
            aria-hidden="true"
          />
        </SidebarGroupLabel>
      </CollapsibleTrigger>
    );
  }

  return (
    <SidebarGroupLabel className="group-data-[collapsible=icon]:hidden">
      {label}
    </SidebarGroupLabel>
  );
}

export function HelpMenuSection() {
  const [isOpen, setIsOpen] = useState(false);
  const setSlotEl = useHelpMenuSlotTarget();
  // Every item in this menu is portaled in by its own trigger component. The
  // flex column with explicit `order` per item lets those slots sit at their
  // declared HELP_MENU_ORDER positions regardless of mount order.
  const menu = <SidebarMenu ref={setSlotEl} className="flex flex-col" />;

  return (
    // Drop the SidebarGroup padding in collapsed (icon) mode so the help
    // icons align with the navigation items in SidebarContent. SidebarFooter
    // already adds `p-2`, and stacking another `p-2` from the group shifts
    // the 32px buttons 8px to the right relative to the 48px sidebar.
    <SidebarGroup className="group-data-[collapsible=icon]:p-0">
      {HELP_MENU_COLLAPSIBLE ? (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <HelpSectionLabel collapsible />
          <CollapsibleContent>{menu}</CollapsibleContent>
        </Collapsible>
      ) : (
        <>
          <HelpSectionLabel collapsible={false} />
          {menu}
        </>
      )}
    </SidebarGroup>
  );
}
